import { HttpClient, HttpParams } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { NotificationAction, NotificationCategory, NotificationModel, ReleaseNoteNotificationModel, SystemNotificationModel } from "@lib/notification/models";
import { EnvironmentService } from "@lib/shared/data-access";
import { IncidentAssignmentSubscriptionModel, IncidentReviewSubscriptionModel } from "@lib/incident/models";
import { Observable, tap } from "rxjs";
import { NotificationPreferenceModel } from "@lib/notification/models";

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  // signals
  private notificationSidebarVisibleSourceSignal = signal(false);
  public notificationSidebarVisibleComputedSignal = computed(() => { return this.notificationSidebarVisibleSourceSignal() });

  private systemNoficiationsSourceSignal = signal<SystemNotificationModel[]>([]);
  public systemNotificationsComputedSignal = computed(() => {
    //sort by addedOn desc
    return this.systemNoficiationsSourceSignal().sort((a, b) => {
      if (!a.createdOn) return -1;  // a is considered less if no date
      if (!b.createdOn) return 1;   // b is considered less if no date
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    });
  }
  );
  public unreadSystemNotificationCountComputedSignal = computed(() => {
    return this.systemNotificationsComputedSignal().filter(n => !n.isRead).length;
  });

  private notificationsSourceSignal = signal<NotificationModel[]>([]);
  public notificationsComputedSignal = computed(() => {
    return this.notificationsSourceSignal().sort((a, b) => {
      if (!a.createdOn) return -1;  // a is considered less if no date
      if (!b.createdOn) return 1;   // b is considered less if no date
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    });
  });

  private httpClient = inject(HttpClient);
  private environmentService = inject(EnvironmentService);

  public showNotificationSidebar() {
    this.notificationSidebarVisibleSourceSignal.set(true);
  }

  public hideNotificationSidebar() {
    this.notificationSidebarVisibleSourceSignal.set(false);
  }

  public getNotificationsForCurrentUser() {
    return this.httpClient
      .get<NotificationModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications`)).pipe(
        tap((response: NotificationModel[]) => {
          this.notificationsSourceSignal.set(response);
        })
      )
  }

  public addNotification(notification: NotificationModel) {
    //Add the new notification to the top of the list if it doesn't already exist
    this.notificationsSourceSignal.update(existingNotifications => {
      if (!existingNotifications.some(n => n.id === notification.id)) {
        return [notification, ...existingNotifications];
      }
      return existingNotifications;
    });
  }

  public updateNotificationsStatus(notifications: NotificationModel[]) {
    return this.httpClient.put(`${this.environmentService.pathfinderApiUrl}/api/v1/notifications/status`, notifications).pipe(
      tap(() => {
        this.notificationsSourceSignal.update(existingNotifications => {
          // Map through existing notifications and update them if they are present in the response
          const updatedNotifications = existingNotifications.map(existingNotification => {
            // Find the corresponding response notification by id
            const responseNotification = notifications.find(res => res.id === existingNotification.id);
            if (responseNotification) {
              // If found, replace the existing notification with the updated one from response
              return { ...existingNotification, readOn: responseNotification.readOn, isRead: responseNotification.isRead };
            }
            // If not found in the response, return the original notification
            return existingNotification;
          });
          // Return the updated notifications array to update the state
          return updatedNotifications;
        });
      })
    );
  }

  //#region reviews

  public getIncidentReviewSubscriptionsByReviewId(incidentReportReviewId: number) {
    return this.httpClient
      .get<IncidentReviewSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-reviews/review/${incidentReportReviewId}`))
  }

  public getIncidentReviewSubscriptionsForCurrentUser() {
    return this.httpClient
      .get<IncidentReviewSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-reviews`))
  }

  public createIncidentReportReviewSubscriptions(newSubscriptions: IncidentReviewSubscriptionModel[]) {
    return this.httpClient
      .post<IncidentReviewSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-reviews`), newSubscriptions)
  }

  public updateIncidentReportReviewsSubscriptionsByReviewId(updatedSubscriptions: IncidentReviewSubscriptionModel[], incidentReportReviewId: number) {
    return this.httpClient
      .put<IncidentReviewSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-reviews/${incidentReportReviewId}`), updatedSubscriptions)
  }
  //#endregion

  //#region assignments

  public getIncidentAssignmentsSubscriptionsByAssignmentId(incidentReportAssignmentId: number) {
    return this.httpClient
      .get<IncidentAssignmentSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-assignments/assignment/${incidentReportAssignmentId}`))
  }

  public getIncidentAssignmentsSubscriptionsByEmployeeId(employeeId: number) {
    return this.httpClient
      .get<IncidentAssignmentSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-assignments/employee/${employeeId}`))
  }

  public createIncidentReportAssignmentsSubscriptions(newSubscriptions: IncidentAssignmentSubscriptionModel[]) {
    return this.httpClient
      .post<IncidentAssignmentSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-assignments`), newSubscriptions)
  }

  public updateIncidentReportAssignmentsSubscriptionsByAssignmentId(updatedSubscriptions: IncidentAssignmentSubscriptionModel[], incidentReportAssignmentId: number) {
    return this.httpClient
      .put<IncidentAssignmentSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-assignments/${incidentReportAssignmentId}`), updatedSubscriptions)
  }

  public deleteIncidentAssignmentsSubscriptionsByAssignmentId(incidentReportAssignmentId: number) {
    return this.httpClient
      .delete<IncidentAssignmentSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-assignments/assignment/${incidentReportAssignmentId}`))
  }

  public deleteIncidentAssignmentsSubscriptionsByAssignmentIdForEmployeeId(incidentReportAssignmentId: number, employeeId: number) {
    return this.httpClient
      .delete<IncidentAssignmentSubscriptionModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/incident-report-assignments/assignment/${incidentReportAssignmentId}/employee/${employeeId}`))
  }
  //#endregion


  //System notifications will only last for the session and will not be stored in the database.
  public addSystemNotification(systemNotification: SystemNotificationModel) {
    this.systemNoficiationsSourceSignal.update(existingNotifications => {
      return [systemNotification, ...existingNotifications];
    });

    //Update the local storage with the new notification
    const existingNotifications = JSON.parse(localStorage.getItem('system-notifications') || '[]') as SystemNotificationModel[];
    existingNotifications.push(systemNotification);
    localStorage.setItem('system-notifications', JSON.stringify(existingNotifications));
  }

  public setSystemNotifications(systemNotifications: SystemNotificationModel[]) {
    this.systemNoficiationsSourceSignal.set(systemNotifications);
    //Set the local storage with the new notifications
    localStorage.setItem('system-notifications', JSON.stringify(systemNotifications));
  }

  public removeSystemNotification(systemNotification: SystemNotificationModel) {
    this.systemNoficiationsSourceSignal.update(existingNotifications => {
      return existingNotifications.filter(n => n.id !== systemNotification.id);
    });
    //Remove from local storage
    const existingNotifications = JSON.parse(localStorage.getItem('system-notifications') || '[]') as SystemNotificationModel[];
    const updatedNotifications = existingNotifications.filter(n => n.id !== systemNotification.id);

    if (updatedNotifications.length < 1) {
      this.clearSystemNotifications();
    }
    else {
      localStorage.setItem('system-notifications', JSON.stringify(updatedNotifications));
    }
  }

  public markAllSystemNotificationsAsRead() {
    const updatedNotifications = this.systemNoficiationsSourceSignal().map(n => {
      return { ...n, isNotificationRead: true };
    });

    this.setSystemNotifications([...updatedNotifications]);
  }

  public clearSystemNotifications() {
    this.systemNoficiationsSourceSignal.set([]);
    //Clear local storage
    localStorage.removeItem('system-notifications');
  }

  // cody system notications
  public createReleaseNotificationForUsers(releaseNoteNotification: ReleaseNoteNotificationModel) {
    return this.httpClient.post<void>(
      this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/system`),
      releaseNoteNotification
    );
  }

  //#region subscriptions
  public getNotificationSubscriptions(
    category: NotificationCategory, 
    tenantExternalId: string | null = null, 
    action: NotificationAction | null = null
  ) : Observable<NotificationPreferenceModel[]> {
    let params = new HttpParams().set('category', category);
    if (tenantExternalId) params = params.set('tenantId', tenantExternalId);
    if (action)          params = params.set('action', action);
    return this.httpClient.get<NotificationPreferenceModel[]>(
      this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions`),
      { params }
    );
  }

  public createNotificationSubscriptions(subscriptions: NotificationPreferenceModel[]) : Observable<NotificationPreferenceModel[]> {
    return this.httpClient.post<NotificationPreferenceModel[]>(
      this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/batch`),
      subscriptions
    );
  }
  
  public deleteNotificationSubscriptions(ids: string[]) : Observable<void> {
    return this.httpClient.delete<void>(
      this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/batch`),
      { body: ids }
    );
  }
  
  public updateNotificationSubscriptions(subscriptions: NotificationPreferenceModel[]) : Observable<NotificationPreferenceModel[]> {
    return this.httpClient.put<NotificationPreferenceModel[]>(
      this.environmentService.pathfinderApiUrl.concat(`/api/v1/notifications/subscriptions/batch`),
      subscriptions
    );
  }
  //#endregion
}